import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';
const API_URL = `${API_BASE}/financiero/aplicacion-pagos`;

// Configurar axios para incluir el token de autenticación
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Obtener campus disponibles
 * @returns {Promise} - Lista de campus
 */
export const getCampusDropdown = async () => {
  try {
    const response = await axios.get(`${API_BASE}/financiero/campus-dropdown/`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener campus:', error);
    throw error;
  }
};

/**
 * Obtener estudiantes con adeudos de un campus específico
 * @param {number} campusId - ID del campus
 * @param {string} search - Término de búsqueda opcional (nombre o matrícula)
 * @returns {Promise} - Lista de estudiantes con adeudos
 */
export const getEstudiantesConAdeudos = async (campusId, search = '') => {
  try {
    let url = `${API_URL}/${campusId}/estudiantes/`;
    
    if (search && search.trim() !== '') {
      url += `?search=${encodeURIComponent(search.trim())}`;
    }
    
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error al obtener estudiantes con adeudos:', error);
    throw error;
  }
};

/**
 * Obtener información completa del estudiante y sus cargos pendientes
 * @param {number} estudianteId - ID del estudiante
 * @returns {Promise} - Datos del estudiante y sus adeudos
 */
export const getDetalleEstudiantePagos = async (estudianteId) => {
  try {
    const response = await axios.get(`${API_URL}/estudiante/${estudianteId}/`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener detalle del estudiante:', error);
    throw error;
  }
};

/**
 * Crear un pago y aplicarlo a uno o más cargos
 * @param {object} pagoData - Datos del pago
 * @returns {Promise} - Respuesta del servidor
 * 
 * Formato:
 * {
 *   id_estudiante: number,
 *   id_campus: number,
 *   forma_pago: string ('EFECTIVO' | 'TRANSFERENCIA' | 'DEPOSITO'),
 *   referencia_bancaria: string (opcional),
 *   monto_recibido: number,
 *   fecha_pago: string (YYYY-MM-DD),
 *   hora_pago: string (HH:MM:SS) (opcional),
 *   observaciones: string (opcional),
 *   aplicaciones: [
 *     {
 *       id_cargo: number,
 *       monto_aplicado: number
 *     }
 *   ],
 *   usuario_creacion: number (opcional)
 * }
 */
export const crearPagoYAplicar = async (pagoData) => {
  try {
    const response = await axios.post(`${API_URL}/crear/`, pagoData);
    return response.data;
  } catch (error) {
    console.error('Error al crear pago:', error);
    
    // Extraer mensaje de error del servidor
    if (error.response && error.response.data) {
      throw {
        message: error.response.data.error || 'Error al crear pago',
        details: error.response.data.detalles || error.response.data
      };
    }
    
    throw error;
  }
};

/**
 * Validar datos de pago antes de enviar
 * @param {object} pagoData - Datos del pago
 * @returns {object} - { valid: boolean, errors: array }
 */
export const validarPago = (pagoData) => {
  const errors = [];
  
  if (!pagoData.id_estudiante) {
    errors.push('Debe seleccionar un estudiante');
  }
  
  if (!pagoData.id_campus) {
    errors.push('Debe seleccionar un campus');
  }
  
  if (!pagoData.forma_pago) {
    errors.push('Debe seleccionar una forma de pago');
  }
  
  if (!pagoData.monto_recibido || parseFloat(pagoData.monto_recibido) <= 0) {
    errors.push('El monto recibido debe ser mayor a cero');
  }
  
  if (!pagoData.fecha_pago) {
    errors.push('Debe especificar la fecha de pago');
  }
  
  if (!pagoData.aplicaciones || pagoData.aplicaciones.length === 0) {
    errors.push('Debe seleccionar al menos un concepto para aplicar el pago');
  }
  
  if (pagoData.aplicaciones && pagoData.aplicaciones.length > 0) {
    let totalAplicado = 0;
    
    pagoData.aplicaciones.forEach((app, index) => {
      if (!app.id_cargo) {
        errors.push(`Aplicación ${index + 1}: falta el ID del cargo`);
      }
      
      if (!app.monto_aplicado || parseFloat(app.monto_aplicado) <= 0) {
        errors.push(`Aplicación ${index + 1}: el monto debe ser mayor a cero`);
      }
      
      totalAplicado += parseFloat(app.monto_aplicado || 0);
    });
    
    if (pagoData.monto_recibido && totalAplicado > parseFloat(pagoData.monto_recibido)) {
      errors.push('El total aplicado excede el monto recibido');
    }
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Formatear fecha para mostrar en interfaz
 * @param {string} fecha - Fecha en formato YYYY-MM-DD
 * @returns {string} - Fecha en formato DD/MM/YYYY
 */
export const formatearFecha = (fecha) => {
  if (!fecha) return '';
  const [year, month, day] = fecha.split('-');
  return `${day}/${month}/${year}`;
};

/**
 * Formatear monto para mostrar en interfaz
 * @param {number} monto - Monto numérico
 * @returns {string} - Monto formateado con separador de miles y 2 decimales
 */
export const formatearMonto = (monto) => {
  if (!monto && monto !== 0) return '$0.00';
  return `$${parseFloat(monto).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
